import { Link, useLocation, useNavigate } from 'react-router-dom';
import QuoteForm from './QuoteForm';
import { useUserSession } from '../Auth/userSession';
import styles from './Quote.module.scss';

interface QuoteProps {
  // inquiry: 견적문의 메뉴(/quote) / order: 주문하기 메뉴의 주문서 작성(/order/form)
  mode?: 'inquiry' | 'order';
}

function Quote({ mode = 'inquiry' }: QuoteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [session] = useUserSession();
  const isOrder = mode === 'order';
  const label = isOrder ? '주문' : '문의';
  // 로그인 후 돌아올 때 상품 선택 정보(state)도 함께 유지한다
  const goLogin = () => navigate('/login', { state: { from: location.pathname, fromState: location.state } });

  return (
    <div className={styles.page}>
      {/* PAGE HEAD */}
      <div className={styles.pageHeadWrap}>
        <section className={styles.pageHead}>
          <div className={styles.crumb}>
            <Link to="/">HOME</Link>
            <svg viewBox="0 0 24 24">
              <path d="m9 18 6-6-6-6" />
            </svg>
            {isOrder ? (
              <>
                <Link to="/order">주문하기</Link>
                <svg viewBox="0 0 24 24">
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <span className={styles.crumbHere}>주문서 작성</span>
              </>
            ) : (
              <span className={styles.crumbHere}>견적문의</span>
            )}
          </div>
          {/* <span className={styles.eyebrow}>Quick &amp; Contact</span> */}
          <h1 className={styles.pageTitle}>
            {isOrder ? (
              <>
                Place an <em>Order</em>
              </>
            ) : (
              <>
                Request a <em>Quote</em>
              </>
            )}
          </h1>
          <p className={styles.pageDesc}>
            {isOrder
              ? '주문서를 남겨주시면 담당자가 사양과 금액을 확정한 뒤 1:1 시안을 올려드립니다. 시안 최종 승인 후 결제하시면 제작 · 배송이 진행됩니다.'
              : '견적문의 남겨주시면 담당자가 확인 후 연락처 및 이메일로 답변드립니다. 첨부파일·사이즈·내용물 정보를 함께 보내주시면 보다 정확한 견적 안내가 가능합니다.'}
          </p>
        </section>
      </div>

      {/* LAYOUT */}
      <section className={styles.layout}>
        <div className={styles.layoutInner}>
          <div className={styles.main}>
            {!session && (
              <div className={styles.modeArea}>
                <div className={styles.modeGrid}>
                  <div className={`${styles.modeCard} ${styles.active}`}>
                    <div className={styles.modeIcon}>
                      <svg viewBox="0 0 24 24">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21a8 8 0 0 1 16 0" />
                      </svg>
                    </div>
                    <div className={styles.modeBody}>
                      <div className={styles.modeTitle}>비회원으로 {label}하기</div>
                      <div className={styles.modeSub}>
                        회원가입 없이 이름과 연락처, 주문 비밀번호만으로 {isOrder ? '주문할' : '견적문의를 남길'} 수
                        있습니다.
                      </div>
                    </div>
                    <span className={styles.modeCheck}>✓</span>
                  </div>
                  <div
                    className={styles.modeCard}
                    role="button"
                    tabIndex={0}
                    onClick={goLogin}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goLogin();
                      }
                    }}
                  >
                    <div className={styles.modeIcon}>
                      <svg viewBox="0 0 24 24">
                        <rect x="3" y="5" width="18" height="14" rx="2" />
                        <path d="m3 8 9 6 9-6" />
                      </svg>
                    </div>
                    <div className={styles.modeBody}>
                      <div className={styles.modeTitle}>로그인 후 {label}하기</div>
                      <div className={styles.modeSub}>
                        회원 로그인 후 {label}하시면 마이페이지에서 문의 · 주문 내역을 모아볼 수 있습니다.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
  
            {!isOrder && (
              <div className={styles.estimateGuide}>
                <strong>견적서 다운로드 안내</strong>
                <p>
                  담당자가 견적 금액을 확정하면 <Link to="/lookup">주문조회</Link>(비회원) 또는{' '}
                  <Link to="/mypage">마이페이지</Link>(회원)에서 공식 견적서를 바로 내려받으실 수 있습니다.
                </p>
              </div>
            )}

            <QuoteForm mode={mode} />
          </div>
  
          {/* SIDE — 숨김 처리 (다시 쓰려면 주석 해제)
          <aside className={styles.side}>
            <div className={styles.sideCard}>
              <div className={styles.sideTitle}>상담 및 문의</div>
              <div className={styles.infoRow}>
                <div className={styles.icon}>
                  <svg viewBox="0 0 24 24">
                    <path d="M22 16.92V20a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3.09a2 2 0 0 1 2 1.72c.13.96.37 1.9.7 2.81a2 2 0 0 1-.45 2.11L8 9.94a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div>
                  <div className={styles.label}>전화</div>
                  <div className={styles.value}>053-216-4126</div>
                </div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.icon}>
                  <svg viewBox="0 0 24 24">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <div className={styles.label}>방문 안내</div>
                  <div className={styles.value}>경상북도 구미시 선산읍</div>
                  <div className={styles.sub}>선주로 129-60</div>
                </div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.icon}>
                  <svg viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>
                <div>
                  <div className={styles.label}>영업 시간</div>
                  <div className={styles.value}>평일 08:30 — 17:30</div>
                  <div className={styles.sub}>점심 12:00 — 13:00 / 토·일·공휴일 휴무</div>
                </div>
              </div>
              <div className={styles.infoRow}>
                <div className={styles.icon}>
                  <svg viewBox="0 0 24 24">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m2 6 10 8 10-8" />
                  </svg>
                </div>
                <div>
                  <div className={styles.label}>E-mail</div>
                  <div className={styles.value}>gn-co@daum.net</div>
                  <div className={styles.sub}>10MB 초과 파일 전달용</div>
                </div>
              </div>
            </div>
  
            <div className={styles.sideCard}>
              <div className={styles.sideTitle}>자주 묻는 질문</div>
              <ul className={styles.qaSide}>
                <li>
                  <div className={styles.qq}>시안은 몇 회까지 수정 가능한가요?</div>
                  <div className={styles.aa}>기본 시안 1회 무료, 추가 시안 작업 시 견적에 함께 안내드립니다.</div>
                </li>
                <li>
                  <div className={styles.qq}>친환경 인증 마크도 인쇄 가능한가요?</div>
                  <div className={styles.aa}>FSC · 환경부 인증 마크 모두 위치 · 사이즈 조절해 인쇄 가능합니다.</div>
                </li>
                <li>
                  <div className={styles.qq}>소량 제작도 가능한가요?</div>
                  <div className={styles.aa}>친환경 박스는 300개부터, 일반 박스는 500개부터 주문 가능합니다.</div>
                </li>
              </ul>
            </div>
          </aside>
          */}
        </div>
      </section>
    </div>
  );
}

export default Quote;
